/**
 * Built-in Help Center knowledge base.
 *
 * Static, zero-cost (no LLM). Each article is a plain object so admins
 * can read, search, and link to a specific feature. Keep articles short
 * and scannable — admins skim, they don't read.
 *
 * Shape:
 *   {
 *     id:        unique slug (used in URLs and routing hints)
 *     title:     short, action-oriented title
 *     category:  category key (see CATEGORIES below)
 *     keywords:  extra terms to match in search (lowercase)
 *     // Hint-path tells the FloatingHelpButton which article(s) to
 *     // surface first when the user is on that page.
 *     pathHints: [string]  — path prefixes this article applies to
 *     body:      newline-separated steps/paragraphs. Lines starting
 *                with "- " render as bullets. "## " renders as a
 *                subheading.
 *   }
 */

export const CATEGORIES = [
  { key: 'getting-started', label: 'Getting Started' },
  { key: 'customers',       label: 'Customers' },
  { key: 'jobs',            label: 'Jobs' },
  { key: 'estimates',       label: 'Estimates' },
  { key: 'invoices',        label: 'Invoices & Payments' },
  { key: 'profitability',   label: 'Profitability' },
  { key: 'warranty',        label: 'Warranty' },
  { key: 'communications',  label: 'Communications' },
  { key: 'team',            label: 'Team & Crew' },
  { key: 'settings',        label: 'Settings & Preferences' },
];

export const HELP_ARTICLES = [
  // ────── Getting Started ──────
  {
    id: 'first-steps',
    title: 'Your first 15 minutes in CrewSync Pro',
    category: 'getting-started',
    keywords: ['onboarding', 'setup', 'welcome', 'new'],
    pathHints: ['/dashboard', '/welcome'],
    body: `Welcome! Here's the fastest path from empty account to first invoice.

## 1. Finish setup
- Open the "Finish setting up" card at the top of your Dashboard
- Knock out the required items (company profile, pricing, first customer)
- Optional items can wait — do them when you have time

## 2. Add your first customer
- Sidebar → Customers → "Add Customer"
- Email is optional, but add it if you have it — it unlocks estimates/invoices by email later

## 3. Build an estimate
- Sidebar → Estimates → "New Estimate"
- Pick the customer, add material and labor items, apply your margin
- Save as draft, then "Send" when ready

## 4. Convert approved estimate → invoice
- When the customer signs/approves, the estimate shows an "Approved" badge
- Click "Create Invoice" on the estimate to carry everything over

## 5. Collect payment
- From the invoice, use "Send for Payment" to email the customer
- Record check/cash with "Add Payment" when it arrives
`,
  },

  // ────── Customers ──────
  {
    id: 'add-customer',
    title: 'Add a new customer',
    category: 'customers',
    keywords: ['create', 'new customer', 'contact'],
    pathHints: ['/customers'],
    body: `## Steps
- Sidebar → Customers → "Add Customer"
- Only Name is required; everything else helps you later
- Email: lets you send estimates, invoices, and reviews
- City/State: used by Service Regions and route planning — don't worry about caps or extra spaces, the app normalizes "austin, tx" → "Austin, TX" automatically

## Tips
- Add multiple email addresses on the customer page to CC a spouse/office manager on every email the app sends
- Use the "Notes" field for anything that doesn't fit in a form — call history, access instructions, gate codes
`,
  },
  {
    id: 'edit-customer-propagates',
    title: 'Why customer edits update jobs and invoices automatically',
    category: 'customers',
    keywords: ['sync', 'update', 'email missing', 'propagate'],
    pathHints: ['/customers'],
    body: `When you change a customer's name, email, or phone on the Customer Dashboard, the app fans the change out to every related record:

- Jobs
- Estimates
- Invoices
- Estimate approvals (pending signatures)
- Payment requests
- Deposits
- Warranty claims
- Review requests
- Customer proposals
- Closeout documents

That means a job created when the customer had no email on file will pick up the new email the moment you save it on the customer. No need to re-open each job.

If you don't see the change on a specific record, refresh that page — the data in the DB is already updated.
`,
  },
  {
    id: 'service-regions',
    title: 'Service Regions: group customers by city',
    category: 'customers',
    keywords: ['regions', 'city', 'map', 'routes', 'duplicate'],
    pathHints: ['/service-regions'],
    body: `Service Regions groups customers by city/state so you can see where your work lives and plan routes.

## Why "austin" and "Austin" show up as one row
We normalize casing automatically. "austin, tx", "AUSTIN, TX", and "Austin, Tx" all collapse into a single "Austin, TX" row with a combined count. New customers you create going forward are stored normalized too.

## Click a city to see its customers
From the sidebar, click any city row to see every customer in that region. Use "Bulk actions" to email the whole region, assign a crew, or create a batch of jobs.
`,
  },

  // ────── Jobs ──────
  {
    id: 'create-job',
    title: 'Create a job',
    category: 'jobs',
    keywords: ['new job', 'scheduled', 'assign crew'],
    pathHints: ['/jobs'],
    body: `## Create from scratch
- Sidebar → Jobs → "New Job"
- Pick the customer, title the job, set a scheduled date
- Assign crew members and set job-site address if different from the customer's address

## Create from an approved estimate (recommended)
- Open the estimate, click "Create Job"
- Every line item, address, and customer detail carries over
- Jobs created this way are automatically linked to the estimate for profitability tracking

## Status lifecycle
- Scheduled → In Progress → Completed
- Completed jobs show up in Profitability for margin analysis
`,
  },
  {
    id: 'time-tracking',
    title: 'Log time against a job',
    category: 'jobs',
    keywords: ['hours', 'labor', 'clock in', 'timesheet'],
    pathHints: ['/jobs', '/time'],
    body: `Logged time feeds two things: payroll AND job profitability.

## From a job page
- Open the job → "Time" tab → "Add Time Entry"
- Pick employee, start/end time, optional notes

## From the Time Tracking page
- Sidebar → Time Tracking → logs across all jobs
- Approve/reject pending entries from here before they count toward profitability

## Why rates matter
- The entry's hourly billing rate × hours = labor cost on the job
- Set accurate rates in Team → Edit Employee for honest margin numbers
`,
  },

  // ────── Estimates ──────
  {
    id: 'create-estimate',
    title: 'Build and send an estimate',
    category: 'estimates',
    keywords: ['quote', 'proposal', 'bid', 'send estimate'],
    pathHints: ['/estimates'],
    body: `## Build it
- Sidebar → Estimates → "New Estimate"
- Pick customer and job type
- Add material items from your catalog (Materials page)
- Add labor via the trade/hours fields — the app applies your markup from Pricing settings
- Review the auto-calculated margin at the bottom

## Send it
- "Send" emails the estimate with a signing link
- The customer sees a web page where they can e-sign and pay the deposit
- You'll get an email alert the moment they approve

## Approval triggers
Once approved, the estimate can be converted to a job and/or invoice in one click.
`,
  },
  {
    id: 'resend-estimate',
    title: 'Resend an estimate the customer missed',
    category: 'estimates',
    keywords: ['resend', 'send again', 'email bounced'],
    pathHints: ['/estimates', '/customers'],
    body: `Two ways to resend:

## From the Estimates page
- Find the estimate → "..." menu → "Resend"
- Keeps the same signing link, just re-emails it

## From the Customer Dashboard
- Open the customer → Estimates tab → click the estimate → "Resend"

Each resend creates a new row in the Email Delivery Log so you can see exactly when the customer received it (and whether it bounced).
`,
  },
  {
    id: 'deposit-options',
    title: 'How deposits work (check and on-site only, for now)',
    category: 'estimates',
    keywords: ['deposit', 'pay by check', 'stripe', 'online payment'],
    pathHints: ['/estimates', '/deposits'],
    body: `When your estimate requires a deposit, the customer sees two choices on the approval page:

- **Mail a check** — their estimate is approved as soon as they sign; you'll follow up with where to mail the check
- **Pay on-site when the crew arrives** — same, with cash/check handed over day one

## Online card payment ("coming soon")
The Stripe online card option is intentionally greyed out until you've connected your Stripe account. Once that's set up, the option flips on automatically and customers can pay instantly.

## Record the deposit when it arrives
- Sidebar → Deposits → "Record Deposit"
- Pick the customer/estimate, enter the amount and method
- The deposit now shows on Dashboard's "Paid This Month" tile and auto-applies to the invoice balance
`,
  },

  // ────── Invoices & Payments ──────
  {
    id: 'create-invoice',
    title: 'Create and send an invoice',
    category: 'invoices',
    keywords: ['bill', 'invoice', 'send for payment'],
    pathHints: ['/invoices'],
    body: `## Fast path (from an approved estimate)
- Open the estimate → "Create Invoice"
- Line items, totals, and deposit history all carry over

## Manual
- Sidebar → Invoices → "New Invoice"
- Pick customer and add line items

## Send it
- "Send for Payment" emails the invoice to the customer
- If a deposit has already been collected, the email automatically shows:
    - Invoice Total
    - Payments / Deposits Received (as a negative line)
    - Balance Due (what the customer still owes)
- The email subject also shows the balance due, not the gross total

## Download as PDF or Word doc
- Open the invoice → "Download PDF" (reads auth-safe through the app, no login issues)
- "Download Word Doc" produces a read-only .docx version
`,
  },
  {
    id: 'record-payment',
    title: 'Record a payment on an invoice',
    category: 'invoices',
    keywords: ['payment', 'paid', 'partial', 'received'],
    pathHints: ['/invoices', '/payments'],
    body: `## Partial payment
- Open the invoice → "Add Payment"
- Enter the amount received, method, and date
- Invoice status flips to "Partial" and the balance due updates everywhere

## Full payment
- Same flow — when the running total matches the invoice total, status flips to "Paid"

## Deposits are payments too
Deposits recorded on an estimate automatically count toward the invoice created from that estimate. You don't need to log the same money twice.
`,
  },
  {
    id: 'invoice-balance-mismatch',
    title: "Why the invoice I'm sending shows the balance, not the full total",
    category: 'invoices',
    keywords: ['balance due', 'deposit', 'already paid'],
    pathHints: ['/invoices'],
    body: `When you hit "Send for Payment" on an invoice that already has a deposit or partial payment logged, the customer sees:

- Invoice Total (the gross)
- Payments / Deposits Received (negative line showing what's been paid)
- **Balance Due** (what's actually owed)

The email subject line also uses the balance due, so customers aren't confused into paying the full amount a second time.

This also applies to the PDF — downloading an invoice with a prior payment shows the same three-line breakdown.
`,
  },

  // ────── Profitability ──────
  {
    id: 'profit-margin',
    title: 'How profit margin is calculated',
    category: 'profitability',
    keywords: ['margin', 'profit', 'dashboard tile', 'coverage'],
    pathHints: ['/dashboard', '/profitability'],
    body: `The Dashboard "Profit Margin" tile and the Profitability tab now use the **exact same calculation** — if the numbers didn't match before, they do now.

## What counts as revenue
- Completed jobs' revenue (sold price → T&M invoiced → T&M projected → estimated, in that order)
- Standalone paid invoices not linked to a completed job

## What counts as cost
- Labor hours × billing rate × (1 + labor burden %)
- Material usage × unit cost
- Equipment usage × hourly/unit rate
- Subcontractor expenses
- Overhead per hour × labor hours

## Coverage %
The "coverage" caption tells you how much of your billed revenue has a cost basis logged. A 30% coverage means 70% of your revenue hasn't had time or materials logged against it — the margin number is directionally right but thin. Log time and materials against jobs for better numbers.
`,
  },
  {
    id: 'overhead-recovered',
    title: 'What "Overhead Recovered" means',
    category: 'profitability',
    keywords: ['overhead', 'recovered', 'monthly target'],
    pathHints: ['/dashboard', '/pricing'],
    body: `Overhead = your monthly fixed costs that have to be paid regardless of how many jobs you do (rent, insurance, admin, truck payments, etc.).

The Dashboard tile shows:
- **$X recovered / $Y target** — how much gross profit this month has gone toward paying the fixed costs
- The bar fills up as the month progresses
- Below 100% at month-end = you lost money that month

## Where to set your target
- Sidebar → Pricing → Overhead Settings
- Enter your total monthly fixed costs
- The app divides it over working hours to compute "overhead per hour" (used in every job's margin)
`,
  },

  // ────── Warranty ──────
  {
    id: 'warranty-claims',
    title: 'Log a warranty claim',
    category: 'warranty',
    keywords: ['warranty', 'callback', 'recall', 'claim'],
    pathHints: ['/warranty'],
    body: `When a past customer calls back about an issue:

## Log it
- Sidebar → Warranty → "New Claim"
- Link to the original job if applicable, so you have full context
- Set severity (minor / major / critical) — this feeds the warranty analytics

## Track resolution
- Add notes, upload photos, attach the follow-up job that fixes it
- Close the claim when the customer is satisfied

## Why it matters
- Warranty cost per job is a leading indicator of quality issues
- Repeat callbacks on the same material or crew member show up in Reports
`,
  },

  // ────── Communications ──────
  {
    id: 'email-delivery-log',
    title: 'Email Delivery Log: see every email the app sent',
    category: 'communications',
    keywords: ['email', 'delivery', 'bounce', 'resend', 'tracking'],
    pathHints: ['/communications', '/settings'],
    body: `Every email the app sends to customers is logged with real-time delivery status from Resend.

## Status badges
- **Sent** — delivered to the recipient's mail provider
- **Opened** — the customer opened the email
- **Clicked** — they clicked a link inside
- **Bounced** — the address doesn't exist (fix it on the customer record!)
- **Complained** — the customer marked it as spam (back off this contact)

## Open in Resend
Each row has an "Open in Resend" deep-link that takes you to Resend's dashboard for low-level debugging.
`,
  },
  {
    id: 'cc-emails',
    title: 'Automatically CC additional emails on everything',
    category: 'communications',
    keywords: ['cc', 'carbon copy', 'additional emails', 'spouse'],
    pathHints: ['/customers'],
    body: `If a customer has more than one email on file (spouse, office manager, bookkeeper), the app will CC all of them on **every** outbound email — estimates, invoices, review requests, approvals, everything.

## How to add
- Open the customer → "Additional Emails" field
- Add one or more comma-separated addresses
- Save → the next email sent fans out automatically

No per-template setup needed. The app handles the CC fan-out globally.
`,
  },

  // ────── Team & Crew ──────
  {
    id: 'invite-team',
    title: 'Invite employees and set their roles',
    category: 'team',
    keywords: ['invite', 'employee', 'crew', 'role', 'permissions'],
    pathHints: ['/team'],
    body: `## Invite someone
- Sidebar → Team → "Invite Employee"
- Enter their email and pick a role
- They get an email with a signup link; after they finish, they appear in your team list

## Roles
- **Admin** — full access, can edit settings and other users
- **Employee** — sees their own time, assigned jobs, and limited customer info
- **Crew lead** — like employee but can also log time for crew members under them

## Hourly rates
- Open an employee → edit → set hourly rate + optional billing rate
- The hourly rate drives payroll; the billing rate drives job profitability
`,
  },

  // ────── Settings & Preferences ──────
  {
    id: 'stay-signed-in',
    title: 'How long will I stay signed in?',
    category: 'settings',
    keywords: ['logout', 'session', 'logged out', 'expire', 'stay signed in'],
    pathHints: [],
    body: `Your session lasts **12 hours** per login.

The session survives:
- Closing and reopening the browser
- Restarting your computer
- Reloading the page

The session ends when:
- 12 hours have passed since login (you'll be sent back to /login)
- You click "Log out" in the sidebar
- A platform admin forces a session reset

## Multiple accounts in different tabs
You can be logged in as two different tenants in two different browser tabs — each tab keeps its own identity. Just open the second account in a brand new tab (not window), sign in there, and the first tab stays as its own user.
`,
  },
  {
    id: 'customize-sidebar',
    title: 'Customize the sidebar',
    category: 'settings',
    keywords: ['sidebar', 'reorder', 'hide', 'customize nav'],
    pathHints: [],
    body: `## Reorder items
- Click "Customize Menu" at the top of the sidebar
- Drag items up and down to match how you work
- Order is saved per-user, so your crew can have a different layout

## Hide what you don't use
- Same screen — toggle any item off
- You can always re-enable from Settings → Sidebar Preferences

## Collapse the sidebar
- Click the chevron at the top-left to shrink it to icons only
- Great for maximizing dashboard space on laptops
`,
  },
  {
    id: 'help-videos',
    title: 'Where are the how-to videos?',
    category: 'getting-started',
    keywords: ['video', 'tutorial', 'watch', 'how-to', 'loom'],
    pathHints: [],
    body: `We ship a library of short Loom walkthroughs for every major feature.

- Sidebar → **App Features & Tips** → categorized video library
- Click a video → opens in-app, closed-captioned
- Admins can leave a note on any video that only their tenant sees ("remind the office crew of this step")

Each video is under 3 minutes. Start with the "Getting Started" category for the fastest onboarding.
`,
  },
];

export const buildHintMatcher = (pathname) => {
  if (!pathname) return () => false;
  return (article) =>
    (article.pathHints || []).some(
      (hint) => pathname === hint || pathname.startsWith(hint + '/')
    );
};
